import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  EyeIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';

import { Duplicate as DuplicateIcon } from '@/components/chariot-ui';
import { MarkdownMedia } from '@/components/markdown/MarkdownPreview';
import { RiskBadge } from '@/components/RiskBadge';
import { Tooltip } from '@/components/Tooltip';
import { AssetStatusComponent } from '@/sections/asset/components/badges';
import { getTypeBadgeLabel } from '@/sections/webpages/utils/badges';
import { Webpage } from '@/types';
import { cn } from '@/utils/classname';

interface WebpageGalleryProps {
  webpages: Webpage[];
  markViewed: (key: string) => void;
  isViewed: (key: string) => boolean;
  /** Open the in-app Focus Review lightbox at this item's index (primary action). */
  onOpenFocus: (index: number) => void;
  /** Open the live site in a new tab. The host owns the validated open (protocol
   * allowlist + `noopener,noreferrer`) — this component never opens the URL itself. */
  onOpenLiveSite: (webpage: Webpage) => void;
}

export function WebpageGallery({
  webpages,
  markViewed,
  isViewed,
  onOpenFocus,
  onOpenLiveSite,
}: WebpageGalleryProps) {
  return (
    <section
      aria-label="Screenshots"
      className="grid grid-cols-1 gap-4 overflow-x-hidden p-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"
    >
      {webpages.map((webpage, index) => {
        const viewed = isViewed(webpage.key);
        const typeLabel = getTypeBadgeLabel(webpage.type);
        return (
          <div
            key={webpage.key}
            className="group border-chariot-stroke bg-chariot-background-primary flex flex-col overflow-hidden rounded-md border"
          >
            <button
              type="button"
              aria-label={`Open review for ${webpage.url}`}
              onClick={() => {
                markViewed(webpage.key);
                onOpenFocus(index);
              }}
              className={cn(
                'flex grow cursor-pointer flex-col text-left',
                'transition-all duration-200 will-change-transform',
                'active:scale-[0.99] motion-safe:hover:scale-[1.02] motion-safe:hover:shadow-md',
                'focus-visible:ring-chariot-brand-primary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset'
              )}
            >
              <div className="bg-chariot-background-secondary relative flex h-40 items-center justify-center overflow-hidden">
                <div className="absolute top-2 right-2 z-10">
                  {/* The score comes straight off the Webpage node (ENG-4395).
                      An absent riskScore renders the neutral "—" chip. */}
                  <RiskBadge score={webpage.riskScore} />
                </div>
                {viewed && (
                  <span className="text-chariot-text-secondary bg-chariot-background-secondary absolute top-2 left-2 z-10 flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs">
                    <EyeIcon className="size-3.5" />
                    Viewed
                  </span>
                )}
                {webpage.screenshot ? (
                  // Plain (non-interactive) img — thumbnail={false} drops the dead
                  // nested role=button (a11y). Parent-scoped utilities make the
                  // plain `<img>` fill + crop the h-40 well (it ships only
                  // `max-w-full rounded-lg` on its own).
                  <div
                    className={cn(
                      'h-full w-full [&_img]:h-full [&_img]:w-full [&_img]:rounded-none [&_img]:object-cover',
                      viewed && 'motion-safe:opacity-90'
                    )}
                  >
                    <MarkdownMedia src={webpage.screenshot} thumbnail={false} />
                  </div>
                ) : (
                  <NoSymbolIcon className="text-chariot-text-disabled size-12" />
                )}
              </div>

              <div className="flex flex-col gap-2 p-3">
                <p
                  title={webpage.url}
                  className={cn(
                    'min-w-0 truncate text-sm font-medium',
                    viewed
                      ? 'text-chariot-text-secondary'
                      : 'text-chariot-text-primary'
                  )}
                >
                  {webpage.url}
                </p>

                <div className="flex min-h-5 flex-wrap items-center gap-1.5">
                  {typeLabel && (
                    <span className="bg-chariot-background-secondary text-chariot-text-secondary rounded-full px-1.5 py-0.5 text-xs font-medium">
                      {typeLabel}
                    </span>
                  )}
                  {webpage.service && (
                    <span className="bg-chariot-background-secondary text-chariot-text-secondary rounded-full px-1.5 py-0.5 text-xs font-medium capitalize">
                      {webpage.service}
                    </span>
                  )}
                  {webpage.sso_identified &&
                    Object.keys(webpage.sso_identified).length > 0 && (
                      <CheckCircleIcon
                        aria-label="SSO protection identified"
                        className="text-chariot-brand-primary size-4"
                      />
                    )}
                </div>
              </div>
            </button>

            <div className="border-chariot-stroke mt-auto flex items-center gap-2 border-t px-2 py-1">
              <AssetStatusComponent asset={webpage} />
              <div className="ml-auto flex items-center gap-1">
                <Tooltip title="Copy URL">
                  <button
                    type="button"
                    aria-label="Copy URL"
                    onClick={e => {
                      e.stopPropagation();
                      void navigator.clipboard.writeText(webpage.url);
                    }}
                    className="text-chariot-text-secondary hover:text-chariot-text-primary focus-visible:ring-chariot-brand-primary flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded focus:outline-hidden focus-visible:ring-2"
                  >
                    <DuplicateIcon className="size-4" />
                  </button>
                </Tooltip>
                <Tooltip title="Open live site in new tab">
                  <button
                    type="button"
                    aria-label="Open live site in new tab"
                    onClick={e => {
                      e.stopPropagation();
                      onOpenLiveSite(webpage);
                    }}
                    className="text-chariot-text-secondary hover:text-chariot-text-primary focus-visible:ring-chariot-brand-primary flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded focus:outline-hidden focus-visible:ring-2"
                  >
                    <ArrowTopRightOnSquareIcon className="size-4" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}
